import React, { useState, useEffect } from "react";
import {
   Box,
   Typography,
   Grid,
   Card,
   CardContent,
   Button,
   LinearProgress,
   Stack,
   IconButton,
   Chip,
   Tooltip,
   Divider,
   Paper,
   alpha,
   useTheme,
   Alert,
   Snackbar,
   Avatar
} from "@mui/material";
import {
   Sync as SyncIcon,
   Movie as MovieIcon,
   Theaters as TheaterIcon,
   Timeline as ShowIcon,
   CheckCircle as CheckIcon,
   Error as ErrorIcon,
   CloudDone as SuccessIcon,
   Refresh as RefreshIcon,
   AutoGraph as ProgressIcon,
   Speed as SpeedIcon,
   Storage as DbIcon,
   SettingsSuggest as ConfigIcon,
   DashboardRounded as HomeIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";

import ModernLoader from "../../components/common/ModernLoader";

const SyncCard = ({ title, icon: Icon, type, lastSync, status, onSync, syncing }) => {
   const theme = useTheme();

   return (
      <motion.div whileHover={{ y: -8, transition: { duration: 0.2 } }}>
         <Paper
            sx={{
               p: 4,
               height: "100%",
               display: "flex",
               flexDirection: "column",
               gap: 3,
               position: "relative",
               overflow: "hidden",
               borderRadius: "32px",
               bgcolor: alpha(theme.palette.background.default, 0.5),
               border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
               boxShadow: "0 20px 40px rgba(0,0,0,0.02)",
               "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "4px",
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  opacity: syncing ? 1 : 0
               }
            }}
         >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
               <Box
                  sx={{
                     p: 2, borderRadius: "18px",
                     bgcolor: alpha(theme.palette.primary.main, 0.06),
                     color: "primary.main",
                     display: "flex", alignItems: "center", justifyContent: "center"
                  }}
               >
                  <Icon sx={{ fontSize: 32 }} />
               </Box>
               <Chip
                  label={status === "success" ? "SYNCED" : "PENDING"}
                  color={status === "success" ? "success" : "warning"}
                  size="small"
                  sx={{ fontWeight: 800, fontSize: "0.65rem", borderRadius: "8px" }}
               />
            </Stack>

            <Box>
               <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5, letterSpacing: "-0.02em" }}>{title}</Typography>
               <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Catalog System Hub
               </Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed" }} />

            <Stack spacing={1.5}>
               <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>System Pulse</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                     <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "success.main" }} />
                     <Typography variant="caption" sx={{ fontWeight: 800 }}>OPTIMAL</Typography>
                  </Stack>
               </Box>
               <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>Last Integrity Check</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>{lastSync || "NEVER"}</Typography>
               </Box>
            </Stack>

            <Box sx={{ mt: "auto", pt: 2 }}>
               {syncing ? (
                  <Stack spacing={1.5}>
                     <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: "primary.main" }}>SYNCHRONIZING...</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>74%</Typography>
                     </Box>
                     <LinearProgress variant="indeterminate" sx={{ height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                  </Stack>
               ) : (
                  <Button
                     fullWidth
                     variant="outlined"
                     onClick={() => onSync(type)}
                     sx={{
                        borderRadius: "16px", py: 2, fontWeight: 900, textTransform: "none", fontSize: "14px", border: "2px solid",
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1), borderColor: "primary.main" }
                     }}
                  >
                     Initiate Wave
                  </Button>
               )}
            </Box>
         </Paper>
      </motion.div>
   );
};

const DataSync = () => {
   const theme = useTheme();
   const [loading, setLoading] = useState(true);
   const [syncing, setSyncing] = useState({ movies: false, theaters: false, shows: false, global: false });
   const [syncStatus, setSyncStatus] = useState({ movies: "pending", theaters: "pending", shows: "pending" });
   const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

   useEffect(() => {
      // Initial fetch of sync status could go here
      setTimeout(() => setLoading(false), 800);
   }, []);

   const handleSync = async (type) => {
      setSyncing(prev => ({ ...prev, [type]: true }));
      try {
         let endpoint = "/sync/movies";
         if (type === "theaters") endpoint = "/sync/theaters";
         if (type === "shows") endpoint = "/sync/shows";

         const response = await api.post(endpoint);
         setSyncStatus(prev => ({ ...prev, [type]: "success" }));
         setAlert({ open: true, message: response.data.message || `${type} synchronized successfully`, severity: "success" });
      } catch (error) {
         console.error(`Sync Error [${type}]:`, error);
         setAlert({ open: true, message: `Integrity failure during ${type} synchronization`, severity: "error" });
      } finally {
         setSyncing(prev => ({ ...prev, [type]: false }));
      }
   };

   const handleSyncAll = async () => {
      setSyncing({ movies: true, theaters: true, shows: true, global: true });
      try {
         await api.post("/sync/all");
         setSyncStatus({ movies: "success", theaters: "success", shows: "success" });
         setAlert({ open: true, message: "Full system state synchronization complete", severity: "success" });
      } catch (error) {
         setAlert({ open: true, message: "Critical failure during global synchronization", severity: "error" });
      } finally {
         setSyncing({ movies: false, theaters: false, shows: false, global: false });
      }
   };

   if (loading) return <ModernLoader text="ESTABLISHING CORE LINK" />;

  return (
    <>
      <Box sx={{ maxWidth: "1600px", mx: "auto" }}>

            {/* Hero Command Section */}
            <Paper
               sx={{
                  p: { xs: 4, md: 6 },
                  mb: 6,
                  borderRadius: "48px",
                  background: theme.palette.mode === 'dark' ? `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.background.paper} 100%)` : `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: theme.palette.secondary.contrastText,
                  position: "relative",
                  overflow: "hidden",
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.palette.mode === 'dark' ? "0 25px 80px -12px rgba(0, 0, 0, 0.7)" : "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
               }}
            >
               <Grid container spacing={4} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                  <Grid item xs={12} md={7}>
                     <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <Box sx={{ p: 1, borderRadius: "10px", bgcolor: alpha(theme.palette.secondary.contrastText, 0.1), display: "flex" }}>
                           <DbIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase" }}>DATACENTER INFRASTRUCTURE</Typography>
                     </Stack>
                     <Typography variant="h1" sx={{ fontWeight: 900, mb: 2.5, lineHeight: 1.1, letterSpacing: "-0.04em", fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" } }}>
                        Systems <br />Synchronizer
                     </Typography>
                     <Typography variant="body1" sx={{ opacity: 0.7, maxWidth: 500, fontWeight: 500, fontSize: "1.1rem" }}>
                        Maintain state consistency across all cinematic entities. Execute delta synchronization for movie catalogs, theaters, and theatrical schedules.
                     </Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                      <Paper
                         sx={{
                            p: { xs: 3, md: 4 }, borderRadius: "32px",
                            bgcolor: alpha(theme.palette.secondary.contrastText, 0.05),
                            backdropFilter: "blur(10px)",
                            border: `1px solid ${alpha(theme.palette.secondary.contrastText, 0.1)}`
                         }}
                      >
                         <Stack spacing={3}>
                            <Box>
                               <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 800, letterSpacing: "0.1em" }}>GLOBAL STATUS</Typography>
                               <Typography variant="h4" sx={{ fontWeight: 900 }}>Optimized</Typography>
                            </Box>
                            <Button
                               variant="contained"
                               fullWidth
                               size="large"
                               startIcon={syncing.global ? <RefreshIcon sx={{ animation: "spin 1s linear infinite" }} /> : <SyncIcon />}
                               onClick={handleSyncAll}
                               disabled={syncing.global}
                               sx={{
                                  height: { xs: 54, md: 64 }, borderRadius: "18px", fontSize: "1.05rem", fontWeight: 900,
                                  bgcolor: theme.palette.secondary.contrastText, color: theme.palette.secondary.main,
                                  "&:hover": { bgcolor: alpha(theme.palette.secondary.contrastText, 0.9) }
                               }}
                            >
                               {syncing.global ? "SYNCHRONIZING..." : "Execute Global Sync"}
                            </Button>
                         </Stack>
                      </Paper>
                   </Grid>
               </Grid>
               <Box sx={{ position: "absolute", bottom: -50, right: -50, width: 300, height: 300, borderRadius: "50%", background: alpha(theme.palette.primary.main, 0.2), filter: "blur(60px)" }} />
            </Paper>

            {/* Sync Node Grid */}
            <Grid container spacing={4}>
               <Grid item xs={12} md={4}>
                  <SyncCard
                     title="Movie Universe"
                     icon={MovieIcon}
                     type="movies"
                     status={syncStatus.movies}
                     syncing={syncing.movies}
                     onSync={handleSync}
                     lastSync="14m ago"
                  />
               </Grid>
               <Grid item xs={12} md={4}>
                  <SyncCard
                     title="Venue Network"
                     icon={TheaterIcon}
                     type="theaters"
                     status={syncStatus.theaters}
                     syncing={syncing.theaters}
                     onSync={handleSync}
                     lastSync="2h ago"
                  />
               </Grid>
               <Grid item xs={12} md={4}>
                  <SyncCard
                     title="Timeline Engine"
                     icon={ShowIcon}
                     type="shows"
                     status={syncStatus.shows}
                     syncing={syncing.shows}
                     onSync={handleSync}
                     lastSync="5m ago"
                  />
               </Grid>
            </Grid>

            {/* System Insights */}
            <Grid container spacing={4} sx={{ mt: 4 }}>
               <Grid item xs={12} lg={8}>
                  <Paper sx={{ p: 5, borderRadius: "40px" }}>
                     <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                        <ConfigIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 900 }}>Infrastructure Insights</Typography>
                     </Stack>
                     <Grid container spacing={3}>
                        {[
                           { label: "Sync Latency", val: "240ms", color: theme.palette.success.main },
                           { label: "State Integrity", val: "99.98%", color: theme.palette.primary.main },
                           { label: "Total Asset Count", val: "1.24k", color: theme.palette.warning.main },
                           { label: "Peak Load Hour", val: "19:00", color: theme.palette.secondary.main }
                        ].map((item, idx) => (
                           <Grid item xs={6} md={3} key={idx}>
                              <Box sx={{ p: 3, borderRadius: "24px", bgcolor: alpha(item.color, 0.04), border: `1px solid ${alpha(item.color, 0.1)}` }}>
                                 <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</Typography>
                                 <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{item.val}</Typography>
                              </Box>
                           </Grid>
                        ))}
                     </Grid>
                  </Paper>
               </Grid>
               <Grid item xs={12} lg={4}>
                  <Paper
                     sx={{
                        p: 4, borderRadius: "40px", bgcolor: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText, height: "100%", display: "flex", flexDirection: "column", gap: 2
                     }}
                  >
                     <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.warning.contrastText, color: "warning.main" }}><SuccessIcon /></Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>System Verified</Typography>
                     </Stack>
                     <Typography variant="body2" sx={{ fontWeight: 800, opacity: 0.8 }}>
                        All systems report operational state. Infrastructure is synchronized with the latest TMDB state.
                     </Typography>
                     <Button variant="contained" sx={{ mt: "auto", borderRadius: "14px", bgcolor: theme.palette.warning.contrastText, color: theme.palette.warning.dark, fontWeight: 900, "&:hover": { bgcolor: alpha(theme.palette.warning.contrastText, 0.9) } }}>Review Audit Logs</Button>
                  </Paper>
               </Grid>
            </Grid>
         </Box>

         <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
            <Alert severity={alert.severity} sx={{ borderRadius: "16px", fontWeight: 700, boxShadow: theme.shadows[5] }}>
               {alert.message}
            </Alert>
         </Snackbar>

    <style>{`
          @keyframes spin {
             from { transform: rotate(0deg); }
             to { transform: rotate(360deg); }
          }
       `}</style>
    </>
   );
};

export default DataSync;
